import React, { Fragment, useEffect, useMemo } from 'react';

import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { FormikValues, useFormik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  adminActions,
  adminGetEntitlement,
  adminUpdateEntitlement,
  entitlementSelector,
  resetStatus,
  successSelector,
} from '@pharmaplan/common';

import { DateTime } from 'luxon';
import { formGenerator } from '../../../../helpers/formGenerator';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { Constants, MomentTimeFormats } from '../../../../helpers/Constants';
import CustomBreadcrumbs from '../../../common/CustomBreadcrumbs';
import CustomButton from '../../../common/CustomButton';
import strings from '../../../../localization';
import { showSuccess } from '../../Profile/Pharmacist/PharmacistViewProfile/helper';

import { editEntitlementFieldConfig, editCrumbs } from './helper';
import validator from './validator';
import styles from './styles';

const EditEntitlement = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();

  const { entitlementId } = (state ?? {}) as { entitlementId?: string };

  const entitlement = useAppSelector(entitlementSelector);
  const { lead, creditsUsed, totalCredits, validUntil, status } = entitlement ?? {};
  const { name, email, campaign } = lead ?? {};
  const { name: campaignName } = campaign ?? {};

  const updateSuccess = useAppSelector((store) =>
    successSelector([adminActions.updateEntitlement], store));

  const initialValues = useMemo(
    () =>
      ({
        pharmacyName: name || '',
        pharmacyEmail: email || '',
        campaignName: campaignName || '',
        totalCredits: totalCredits ?? 0,
        creditsUsed: creditsUsed ?? 0,
        validUntil: DateTime.fromISO(validUntil, { zone: 'utc' }) || '',
        status: status || '',
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entitlement],
  );

  const handleSubmit = (values: FormikValues) => {
    if (!entitlementId) {
      return;
    }

    dispatch(
      adminUpdateEntitlement({
        entitlementId,
        totalCredits: Number(values.totalCredits),
        validUntil: values.validUntil
          ? values.validUntil.toFormat(MomentTimeFormats.fullDate)
          : null,
      }),
    );
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validationSchema: validator(),
  });

  useEffect(() => {
    if (entitlementId) {
      dispatch(adminGetEntitlement(entitlementId));
    }
  }, [dispatch, entitlementId]);

  useEffect(() => {
    if (updateSuccess) {
      showSuccess(dispatch, strings.actionCompletedSuccessfully);
      dispatch(resetStatus([adminActions.updateEntitlement]));
      navigate(Constants.paths.admin.entitlements);
    }
  }, [updateSuccess]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.crumbContainer}>
        <CustomBreadcrumbs crumbs={editCrumbs} />
      </Box>
      <Typography sx={styles.title}>{strings.editEntitlement}</Typography>

      <Grid container xs={12}>
        {editEntitlementFieldConfig(formik).map((item) => {
          const { key } = item;
          return <Fragment key={key}>{formGenerator(item, formik)}</Fragment>;
        })}
      </Grid>

      <Grid container xs={12} sx={styles.buttonContainer}>
        <CustomButton label={strings.save} onClick={formik.handleSubmit} />
      </Grid>
    </Box>
  );
};

export default EditEntitlement;
